import { useThree } from '@react-three/fiber';
import { useCallback, useMemo } from 'react';

import { usePlayerSettingsContext } from '@cartwhl/3d/player/hooks/playerSettingsContext';
import type { PlayerStore } from '@cartwhl/3d/player/store/playerStore';

import { createVideoFromBlobs } from './createVideoFromBlobs';
import type { ProgressInfo } from './useFfmpeg';
import useFfmpeg from './useFfmpeg';
import { ensureEvenVideoDimensions } from './videoExportDimensions';

function dataURLtoBlob(dataUrl: string) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
}

function waitNextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

export function useMotionEditorCanvasRecorder(store: PlayerStore) {
  const { gl, scene, camera } = useThree();
  const {
    settings: { playback, environment },
  } = usePlayerSettingsContext();
  const { loadFfmpeg, createProgressTracker } = useFfmpeg();

  const recordVideo = useCallback(
    async (
      onProgress?: (progress: ProgressInfo) => void,
      signal?: AbortSignal,
    ) => {
      const { width, height } = ensureEvenVideoDimensions(
        gl.domElement.width,
        gl.domElement.height,
      );
      const frameRate = playback.frameRate;
      const playbackSpeed = playback.playbackSpeed ?? 1;
      const backgroundColor = environment.useBackgroundGradient
        ? undefined
        : environment.backdropHexColor;

      const durationFrames = store.state.transport.duration.frames;
      if (!durationFrames || !width || !height) {
        return undefined;
      }

      const params = new URLSearchParams(window.location.search);
      const mp4MaxFramesParam = params.get('mp4MaxFrames');
      const mp4MaxFrames = mp4MaxFramesParam
        ? parseInt(mp4MaxFramesParam, 10)
        : undefined;

      onProgress?.({ percentage: 1, time: '00:00.00' });

      const ffmpeg = await loadFfmpeg();

      const startTime = Date.now();

      const recordedChunks: Blob[] = [];
      const maxFrames =
        mp4MaxFrames && durationFrames > mp4MaxFrames
          ? mp4MaxFrames
          : durationFrames;

      store.setFrame(0);
      await waitNextAnimationFrame();

      const reportRecordingProgress = (currentFrame: number) => {
        if (onProgress) {
          const recordingPercentage = (currentFrame / maxFrames) * 20;
          const elapsedSeconds = currentFrame / frameRate;
          const minutes = Math.floor(elapsedSeconds / 60);
          const seconds = (elapsedSeconds % 60).toFixed(2);
          const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.padStart(5, '0')}`;
          onProgress({
            percentage: recordingPercentage,
            time: timeString,
          });
        }
      };

      for (let recFrame = 0; recFrame < maxFrames; recFrame++) {
        if (signal?.aborted) {
          return undefined;
        }

        store.setFrame(recFrame);
        await waitNextAnimationFrame();
        gl.render(scene, camera);
        const data = gl.domElement.toDataURL();
        recordedChunks.push(dataURLtoBlob(data));

        if (recFrame % 10 === 0 || recFrame === maxFrames - 1) {
          reportRecordingProgress(recFrame + 1);
        }
      }

      if (signal?.aborted) {
        return undefined;
      }

      const reportVideoCreationProgress = (progress: ProgressInfo) => {
        if (onProgress) {
          const scaledPercentage = 20 + progress.percentage * 0.8;
          const totalElapsedMs = Date.now() - startTime;
          const totalElapsedSeconds = totalElapsedMs / 1000;
          const totalMinutes = Math.floor(totalElapsedSeconds / 60);
          const totalSeconds = (totalElapsedSeconds % 60).toFixed(2);
          const totalTimeString = `${totalMinutes.toString().padStart(2, '0')}:${totalSeconds.padStart(5, '0')}`;
          onProgress({
            percentage: scaledPercentage,
            time: totalTimeString,
          });
        }
      };

      const video = await createVideoFromBlobs(
        recordedChunks,
        'output.mp4',
        ffmpeg,
        width,
        height,
        frameRate,
        playbackSpeed,
        backgroundColor,
        reportVideoCreationProgress,
        createProgressTracker,
      );

      return video;
    },
    [
      camera,
      createProgressTracker,
      environment.backdropHexColor,
      environment.useBackgroundGradient,
      gl,
      loadFfmpeg,
      playback.frameRate,
      playback.playbackSpeed,
      scene,
      store,
    ],
  );

  return useMemo(() => ({ recordVideo }), [recordVideo]);
}
